import type { SkillSuggestionsResult } from './skill-suggestions.type';

export function htmlToPlainText(html: string): string {
  if (!html) {
    return '';
  }
  const withBreaks = html.replace(/<\/(p|li|div|h[1-6]|tr)>|<br\s*\/?>/gi, '\n');
  const doc = new DOMParser().parseFromString(withBreaks, 'text/html');
  doc.querySelectorAll('script, style').forEach((element) => element.remove());
  const text = doc.body.textContent ?? '';
  return text
    .replace(/[ \t\f\v ]+/g, ' ')
    .replace(/ ?\n ?/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export const SKILL_SUGGESTIONS_EARLY_TRIGGER_MIN_CHARS = 200;

export function isSubstantialDescription(description: string | null): boolean {
  return htmlToPlainText(description ?? '').length >= SKILL_SUGGESTIONS_EARLY_TRIGGER_MIN_CHARS;
}

export function normalizeSuggestionText(text: string): string {
  return text.replace(/\s+/g, ' ').trim().toLocaleLowerCase();
}

export function hasUsableContent(title: string | null, description: string | null): boolean {
  return Boolean(title?.trim()) && Boolean(htmlToPlainText(description ?? ''));
}

// Whitespace and markup differences must not count as a content change, or the editor
// re-formatting the description on load would trigger a second request.
export function buildContentFingerprint(title: string | null, description: string | null): string {
  return `${normalizeSuggestionText(title ?? '')}\n${normalizeSuggestionText(
    htmlToPlainText(description ?? ''),
  )}`;
}

export function cleanSuggestionList(values: unknown): string[] {
  if (!Array.isArray(values)) {
    return [];
  }

  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    if (typeof value !== 'string') {
      continue;
    }
    const text = value.replace(/\s+/g, ' ').trim();
    const key = normalizeSuggestionText(text);
    if (!key || seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(text);
  }
  return result;
}

export function cleanSuggestionResult(result: {
  knowledgeDisciplines?: unknown;
  technologiesMachines?: unknown;
}): SkillSuggestionsResult {
  return {
    knowledgeDisciplines: cleanSuggestionList(result.knowledgeDisciplines),
    technologiesMachines: cleanSuggestionList(result.technologiesMachines),
  };
}
